import { Checkbox } from "@repo/ui/components/Checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@repo/ui/components/Table";
import { cn } from "@repo/ui/lib/utils";
import difference from "lodash/difference";
import isEmpty from "lodash/isEmpty";
import union from "lodash/union";
import without from "lodash/without";

export type ColumnDef<TData> = {
  id: string;
  header?: () => React.ReactNode;
  cell: (row: TData) => React.ReactNode;
  width?: string;
  align?: "left" | "center" | "right";
};

export type DataTableSelection = {
  value: string[];
  onChange: (value: string[]) => void;
};

export type DataTableProps<TData> = {
  data?: TData[];
  loading?: boolean;
  columns: ColumnDef<TData>[];
  visibleColumns: string[];
  getRowId: (row: TData) => string;
  selection?: DataTableSelection;
  actions?: (row: TData) => React.ReactNode;
  emptyState?: React.ReactNode;
  onRowClick?: (row: TData, rowId: string) => void;
  className?: string;
};

export const DataTable = <TData,>(props: DataTableProps<TData>) => {
  const pageRowIds = props.data?.map(props.getRowId) ?? [];

  const selectedRowIds = props.selection?.value ?? [];
  const isPageSelected =
    props.selection && !!props.data?.length && isEmpty(difference(pageRowIds, selectedRowIds));

  const isRowSelected = (rowId: string) => {
    return props.selection && selectedRowIds.includes(rowId);
  };

  const handlePageToggle = () => {
    if (!props.selection) return;

    if (isPageSelected) {
      props.selection.onChange(difference(selectedRowIds, pageRowIds));
    } else {
      props.selection.onChange(union(selectedRowIds, pageRowIds));
    }
  };

  const handleRowToggle = (rowId: string) => {
    if (!props.selection) return;

    if (!isRowSelected(rowId)) {
      props.selection.onChange([...selectedRowIds, rowId]);
    } else {
      props.selection.onChange(without(selectedRowIds, rowId));
    }
  };

  if (props.loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center rounded-lg border">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!props.data?.length) {
    return (
      props.emptyState ?? (
        <div className="flex min-h-[400px] items-center justify-center rounded-lg border">
          <div className="text-muted-foreground">No data</div>
        </div>
      )
    );
  }

  return (
    <div className={cn("min-h-0 flex-1 overflow-auto rounded-lg", props.className)}>
      <Table>
        <TableHeader>
          <TableRow>
            {/* Checkbox column */}
            {props.selection && (
              <TableHead className="w-6">
                <Checkbox checked={isPageSelected} onCheckedChange={handlePageToggle} />
              </TableHead>
            )}

            {/* Regular columns */}
            {props.columns
              .filter((col) => props.visibleColumns.includes(col.id))
              .map((col) => (
                <TableHead
                  key={col.id}
                  style={{ width: col.width }}
                  className={col.align === "right" ? "text-right" : undefined}
                >
                  {col.header?.()}
                </TableHead>
              ))}

            {/* Actions column */}
            {props.actions && <TableHead className="text-right" />}
          </TableRow>
        </TableHeader>

        <TableBody>
          {props.data.map((row) => {
            const rowId = props.getRowId(row);

            return (
              <TableRow
                key={rowId}
                className={cn(
                  (props.selection ?? props.onRowClick) && "cursor-pointer",
                  props.selection && isRowSelected(rowId) && "bg-primary/10 hover:bg-primary/20",
                )}
                onClick={() => {
                  if (props.onRowClick) {
                    props.onRowClick(row, rowId);
                    return;
                  }
                  if (props.selection) handleRowToggle(rowId);
                }}
              >
                {/* Checkbox cell */}
                {props.selection && (
                  <TableCell>
                    <Checkbox checked={isRowSelected(rowId)} />
                  </TableCell>
                )}

                {/* Regular cells */}
                {props.columns
                  .filter((col) => props.visibleColumns.includes(col.id))
                  .map((col) => (
                    <TableCell
                      key={col.id}
                      className={col.align === "right" ? "text-right" : undefined}
                    >
                      {col.cell(row)}
                    </TableCell>
                  ))}

                {/* Actions cell - stop propagation so clicking doesn't toggle selection */}
                {props.actions && (
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    {props.actions(row)}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};
